import { useEffect, useState } from "react";
import { useAuthHeader, useAuthUser } from "../../../../Services/Auth/sessionAuth";
import { Button, Form, Space, Spin, Tabs, Tag, Tooltip } from 'antd';
import Utilisateur from "../../../../Models/Utilisateur";
import type { TabsProps } from 'antd';
import { UserOutlined, EyeOutlined } from '@ant-design/icons';
import SweetAlert from "sweetalert2";
import { Crypter } from "../../../../Functions/Security/Crypter";
import Data from '../../../../Data/Data.json';
import axios from "axios";
import Tab1 from "./Tab1";
import { Typography, IconButton, Stack, Grid, Box } from "@mui/material";
import { normalizeKeysToUppercase } from "../../../../utilitaires/globalFunction";
import dayjs from 'dayjs';
import { ActifDocumentType, extractActifDocumentRows, getActifDocumentLabel, loadActifDocumentTypes, mapActifDocumentsToPreview, toDocumentDataUri } from '../../../../Services/actifDocumentTypes';
import AssetDocumentPreviewModal from '../../../AssetDocumentPreviewModal';

const buildDocuments = (row: any, definitions: ActifDocumentType[]) => {
    // Si on a des documents réels, les utiliser
    const documents = row.Documents || row.DOCUMENTS || row.documents || [];
    if (documents.length > 0) {
        return documents.map((document: any) => ({
            label: getActifDocumentLabel(document, definitions),
            base64: toDocumentDataUri(document)
        })).filter((document: any) => document.base64);
    }
    
    // Sinon, créer des entrées factices basées sur NbDocuments pour l'affichage du compteur
    const nbDocuments = row.NBDOCUMENTS || row.NbDocuments || 0;
    if (nbDocuments > 0) {
        return Array.from({ length: nbDocuments }, (_, index) => ({
            label: `Document ${index + 1}`,
            base64: null // Pas de contenu pour les documents factices
        }));
    }
    
    return [];
};

const ValidationActifPage = () => {
    const auth = useAuthUser();
    const Perss: any = auth();
    const authHeader = useAuthHeader();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [showRejet, setShowRejet] = useState(false);
    const [listeAttente, setListeAttente] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedActif, setSelectedActif] = useState<any>(null);
    const [Annulactv, setAnnule] = useState(true);
    const [documentDefinitions, setDocumentDefinitions] = useState<ActifDocumentType[]>([]);

    const [docModal, setDocModal] = useState<{ open: boolean; docs: { label: string; base64: string | null }[]; activeIndex: number | null }>({
        open: false, docs: [], activeIndex: null
    });

    // Fonction pour charger les documents d'un actif et ouvrir la modal
    const openDocsModalWithLoad = async (row: any) => {
        // Si les documents sont déjà chargés avec leur contenu, les utiliser directement
        const rowDocuments = row.Documents || row.DOCUMENTS || row.documents || [];
        if (rowDocuments.length > 0) {
            const docs = buildDocuments(row, documentDefinitions);
            if (docs.length > 0) {
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
                return;
            }
        }
        // Charger les documents depuis l'API (GetActifWithDocuments si dispo, sinon GetActifsByValidation)
        try {
            setLoading(true);
            const getActifWithDocuments = (Data.ApiUrl as any).GetActifWithDocuments;
            const useNewEndpoint = !!getActifWithDocuments;
            const headers: any = {
                'Authorization': authHeader(),
                'CypherCodeActif': Crypter.encrypt(row.CODEACTIF)
            };
            if (!useNewEndpoint) {
                headers['CypherStatutValidation'] = Crypter.encrypt(row.STATUTVALIDATION || 'EN_ATTENTE');
            }
            const response = await axios.get(useNewEndpoint ? getActifWithDocuments : Data.ApiUrl.GetActifsByValidation, { headers });
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
                const docRows = extractActifDocumentRows(res.Result, row.CODEACTIF);
                const docs = mapActifDocumentsToPreview(docRows, documentDefinitions);
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
            } else {
                setDocModal({ open: true, docs: [], activeIndex: null });
            }
        } catch (error) {
            console.error('Erreur chargement documents:', error);
            setDocModal({ open: true, docs: [], activeIndex: null });
        } finally {
            setLoading(false);
        }
    };

    const openDocsModal = (docs: { label: string; base64: string | null }[]) => setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
    const closeDocsModal = () => setDocModal({ open: false, docs: [], activeIndex: null });

    const loadActifs = () => {
    setLoading(true);
    
    // On chiffre la valeur brute
    const encryptedStatut = Crypter.encrypt('EN_ATTENTE');

    axios.get(Data.ApiUrl.GetActifsByValidation, {
        // 1. On retire l'objet params qui ne fonctionnait pas
        // 2. On place le paramètre dans les headers
        headers: { 
            'Authorization': authHeader(),
            'CypherStatutValidation': encryptedStatut // <--- C'EST ICI LA SOLUTION
        }
    }).then((response) => {
        const decrypted = Crypter.decrypt(response.data);
        const res = JSON.parse(decrypted).Value;
        
        if (res.StatutCode === 200) {
            const rawData = res.Result.Table || res.Result || [];
            const normalized = normalizeKeysToUppercase(Array.isArray(rawData) ? rawData : [rawData]);
            
            // DEBUG: Log pour vérifier la structure des données
            console.log('DEBUG ValidationActif - Données brutes reçues:', rawData);
            console.log('DEBUG ValidationActif - Premier actif:', normalized[0]);
            console.log('DEBUG ValidationActif - Propriétés Documents:', normalized[0]?.Documents);
            console.log('DEBUG ValidationActif - Propriétés DOCUMENTS:', normalized[0]?.DOCUMENTS);
            
            setListeAttente(normalized);
        } else {
            // Optionnel : Message si la liste est vide
            setListeAttente([]);
            if (res.StatutCode !== 404) {
                 SweetAlert.fire("Info", res.Message, "info");
            }
        }
        setLoading(false);
    }).catch((err) => {
        console.error("Erreur réseau/traitement:", err);
        setLoading(false);
        SweetAlert.fire("Erreur", "Le serveur ne répond pas correctement", "error");
    });
};

    useEffect(() => {
        loadActifs();
        loadActifDocumentTypes(authHeader()).then(setDocumentDefinitions).catch(() => setDocumentDefinitions([]));
    }, []);

    // --- BOUTON ANNULER : réinitialise tout, y compris la sélection en cours ---
    function HandleAnnul() {
        setAnnule(true);
        setSelectedActif(null);
        setSelectedRowKeys([]);
        setShowRejet(false);
        form.resetFields();
    }

    // --- CONTEXTE CATÉGORIE PARENTE : appel séparé au moment de la sélection ---
    const loadCategorieContext = (codeCategorie: string) => {
        axios.get(Data.ApiUrl.GetByCodeCategorie, {
            // Alt (si GetCategorieActif seul n'existe pas / ne convient pas) :
            // Data.ApiUrl.GetCategorieAvecActifs — dans ce cas ne garder que .Result[0].Categorie
            headers: { 'Authorization': authHeader(), 'CypherCodeCategorie': Crypter.encrypt(codeCategorie) }
        }).then((response) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
                // Structure exacte à confirmer côté backend (objet direct ou tableau Result[0])
                const d = res.Result;
                form.setFieldsValue({
                    LIBELLE: d.Libelle,
                    FAMILLE: d.CodeFamille,
                    REGLESUSAGE: d.ReglesUsage,
                    lib_FAMILLE: d.LibelleFamille, 
                    ESTLICITE: d.EstLicite,
                    FINANCEMENT_ELIGIBLES: d.Financements,
                    ESTACTIF: d.EstActif
                });
                setTimeout(() => {
                form.setFieldValue("LIBELLE", d.Libelle);
                // Si ton champ SearchModal de catégorie a aussi besoin d'un libellé à droite :
                form.setFieldValue("lib_CODECATEGORIE", d.Libelle);
            }, 100);
            }
        }).catch((err) => console.error("Erreur chargement contexte catégorie:", err));
    };

    // --- SÉLECTION VIA BOUTON RADIO ---
    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    // 1. On ne garde que la dernière clé sélectionnée (sélection exclusive)
    setSelectedRowKeys(newSelectedRowKeys);

    // 2. On récupère les données de la ligne correspondante
    const index = newSelectedRowKeys[0] as number;
    const actifSelectionne = listeAttente[index];

    if (actifSelectionne) {
        setSelectedActif(actifSelectionne);
        setShowRejet(false);

        // 3. REMPLISSAGE DES CHAMPS EN HAUT
        // On mappe les données de l'actif vers le formulaire
        form.setFieldsValue({
            ...actifSelectionne,
            // On s'assure que les noms correspondent à tes InputForm du Tab1
            CODECATEGORIE: actifSelectionne.CODECATEGORIE,
            //LIBELLE: actifSelectionne.LIBELLE,
            //FAMILLE: actifSelectionne.FAMILLE,
           // lib_FAMILLE: actifSelectionne.LIBELLEFAMILLE, // Important pour SearInputG
            LOCALISATIONCOURANTE: actifSelectionne.LOCALISATIONCOURANTE,
            UPDATEDBY: actifSelectionne.ENREGISTREPAR,
            DATEMODIFICATION: actifSelectionne.DATEENREGISTREMENT 
                ? dayjs(actifSelectionne.DATEENREGISTREMENT).format('DD/MM/YYYY HH:mm') 
                : '',
            COMMENTAIRE_VALIDATEUR: ""  // On vide le motif de rejet pour le nouveau dossier
        });

        // 4. CHARGEMENT DU CONTEXTE (Libellé Catégorie, Famille, etc.)
        if (actifSelectionne.CODECATEGORIE) {
            loadCategorieContext(actifSelectionne.CODECATEGORIE);
        }
    }
};

    // --- DÉCISION : payload aligné sur ActifValidationDto ---
    const handleDecision = (decision: 'VALIDE' | 'REJETE') => {
        if (!selectedActif) {
            SweetAlert.fire("Attention", "Veuillez sélectionner un actif à traiter.", "warning");
            return;
        }

        if (decision === 'REJETE' && !showRejet) {
        setShowRejet(true); // On affiche le champ
        return; // On arrête la fonction ici pour laisser l'utilisateur saisir le motif
    }

        const commentaire = form.getFieldValue("COMMENTAIRE_VALIDATEUR");

        if (decision === 'REJETE' && (!commentaire || commentaire.trim() === "")) {
            SweetAlert.fire("Motif requis", "Veuillez saisir un commentaire pour justifier le rejet.", "warning");
            return;
        }

        SweetAlert.fire({
            title: decision === 'VALIDE' ? "Confirmer la validation" : "Confirmer le rejet",
            text: `Voulez-vous enregistrer cette décision pour l'actif : ${selectedActif.DESIGNATION} ?`,
            showCancelButton: true,
            confirmButtonColor: decision === 'VALIDE' ? "green" : "#d33",
            confirmButtonText: "Confirmer", cancelButtonText: "Annuler", icon: 'question'
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                const payload = {
                    CodeActif: selectedActif.CODEACTIF,
                    StatutValidation: decision,
                    CommentaireValidation: commentaire || "",
                    ValidePar: Perss.coduti
                };

                const dataChiffree = Crypter.encrypt(JSON.stringify(payload));
                
                axios.post(Data.ApiUrl.ValiderActif, JSON.stringify(dataChiffree), {
    headers: { 
        'Authorization': authHeader(), 
        'Content-Type': 'application/json' 
    }
                }).then((response) => {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        
        if (res.StatutCode === 200) {
            SweetAlert.fire("Terminé", res.Message, "success");
            HandleAnnul(); // On utilise ta fonction de reset
            loadActifs();  // On recharge la liste
        } else {
            SweetAlert.fire("Erreur", res.Message, "error");
        }
        setLoading(false);
    }).catch((err) => {
        setLoading(false);
        console.error("Erreur Décryptage/Réseau:", err);
        SweetAlert.fire("Erreur", "Le serveur n'a pas pu traiter la décision.", "error");
    });
}
        });
    };

    const formatMontant = (val: number) => new Intl.NumberFormat('fr-FR').format(val || 0);

    const columns = [
        {
            key: 'CODEACTIF', label: 'Code Actif', width: 30, align: 'center' as const,
            render: (row: any) => (
                <Tooltip placement="top" autoAdjustOverflow={false} title={row?.DESIGNATION || '-'}>
                    <span style={{ fontWeight: 'bold' }}>{row?.CODEACTIF || '-'}</span>
                </Tooltip>
            )
        },
        {
            key: 'DESIGNATION', label: 'Désignation', width: 90,
            render: (row: any) => <div className="whitespace-normal break-words">{row?.DESIGNATION}</div>
        },
        {
    key: 'COUTACHAT', 
    label: "Coût d'achat(XAF)", 
    width: 20, // Augmenté un peu pour la lisibilité
    align: 'right' as const,
    render: (row: any) => {
        // LOGIQUE SENIOR : On teste les 3 variantes possibles envoyées par le C#
        // On convertit en Number pour être sûr que formatMontant fonctionne
        const montant = row?.COUTINITIAL ?? row?.COUTACHAT ?? row?.COUT_ACHAT ?? 0;
        
        return (
            <span style={{ fontWeight: 'bold' }}>
                {formatMontant(Number(montant))}
            </span>
        );
    }
},
{
            key: 'VALEURRESIDUELLE', label: "V. Résiduelle", width: 30, align: 'right' as const,
            render: (row: any) => <span style={{ color: '#666' }}>{formatMontant(row?.VALEURRESIDUELLE)}</span>
        },
        {
            key: 'GRADE', label: 'Grade', width: 30,
            render: (row: any) => <Tag color="blue">{row?.GRADEDESCRIPTION || row?.CODEGRADE}</Tag>
        },
        {
            key: 'CODEFOURNISSEUR', label: 'Fournisseur', width: 30,
            render: (row: any) => (
                <Tooltip placement="top" autoAdjustOverflow={false} title={row?.LIBELLEFOURNISSEUR || row?.lib_CODEFOURNISSEUR}>
                    <span>{row?.CODEFOURNISSEUR}</span>
                </Tooltip>
            )
        },
        {
            key: 'DOCUMENTS', label: 'Documents', width: 30, align: 'center' as const,
            render: (row: any) => {
                const count = row.NBDOCUMENTS ?? row.NbDocuments ?? 0;
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <IconButton size="small" onClick={() => openDocsModalWithLoad(row)} disabled={count === 0 && !row.CODEACTIF}>
                            <EyeOutlined style={{ fontSize: 18, color: count > 0 ? '#2A6596' : '#ccc' }} />
                        </IconButton>
                        <Tag color={count === 0 ? 'default' : 'green'}>{count}</Tag>
                    </Stack>
                );
            }
        },
    ];

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span><UserOutlined /> Dossier Technique & Documents </span>,
            children: <Tab1
                form={form}
                selectedActif={selectedActif}
                listeAttente={listeAttente}
                columns={columns}
                selectedRowKeys={selectedRowKeys}
                onSelectChange={onSelectChange}
                showRejet={showRejet} />
        },
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b> VALIDATION DES ACTIFS PHYSIQUES </b></Typography>
            <Spin spinning={loading} size='large' style={{ color: 'green' }}>
                <Form form={form} preserve labelAlign="left">
                    <div className='bG'>
                        <legend><Typography color={"darkgreen"}>Actions</Typography></legend>
                        <Form.Item className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' onClick={() => handleDecision('VALIDE')} block style={{ backgroundColor: 'green', color: 'white' }}>Valider</Button>
                                <Button size='large' onClick={() => handleDecision('REJETE')} block style={{ backgroundColor: 'red', color: 'white' }}>Rejeter</Button>
                                <Button size='large' onClick={loadActifs} block>Actualiser</Button>
                                {/* <Button size='large' disabled={Annulactv} onClick={HandleAnnul} block>Annuler</Button> */}
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <Tabs size='large' type="card" defaultActiveKey="1" items={items} />
                    </div>
                </Form>
            </Spin>

            <AssetDocumentPreviewModal
                open={docModal.open}
                docs={docModal.docs}
                activeIndex={docModal.activeIndex}
                onSelect={(index) => setDocModal(prev => ({ ...prev, activeIndex: index }))}
                onBack={() => setDocModal(prev => ({ ...prev, activeIndex: null }))}
                onClose={closeDocsModal}
            />
        </div>
    );
};

export default ValidationActifPage;